import { API_ROUTES } from '@config/routes';
import api from './api';
import type { Conversation, Message, PaginatedResponse } from '@/types';

export const conversationService = {
  // Get all conversations
  getConversations: async (params?: {
    page?: number;
    pageSize?: number;
    status?: string;
  }): Promise<PaginatedResponse<Conversation>> =>
    api.get(API_ROUTES.CONVERSATIONS.BASE, { params }),

  // Get conversation by ID
  getConversationById: async (id: string): Promise<Conversation> =>
    api.get(API_ROUTES.CONVERSATIONS.BY_ID(id)),

  // Create new conversation
  createConversation: async (data: {
    contactId?: string;
    initialMessage?: string;
    metadata?: Record<string, unknown>;
  }): Promise<Conversation> => api.post(API_ROUTES.CONVERSATIONS.BASE, data),

  // Update conversation
  updateConversation: async (id: string, data: Partial<Conversation>): Promise<Conversation> =>
    api.put(API_ROUTES.CONVERSATIONS.BY_ID(id), data),

  // Delete conversation
  deleteConversation: async (id: string): Promise<void> =>
    api.delete(API_ROUTES.CONVERSATIONS.BY_ID(id)),

  // Get messages for conversation
  getMessages: async (conversationId: string): Promise<Message[]> =>
    api.get(API_ROUTES.CONVERSATIONS.MESSAGES(conversationId)),

  // Send message
  sendMessage: async (
    conversationId: string,
    content: string,
    role: 'user' | 'assistant' = 'user'
  ): Promise<Message> =>
    api.post(API_ROUTES.CONVERSATIONS.MESSAGES(conversationId), { content, role }),

  // Archive conversation
  archiveConversation: async (id: string): Promise<Conversation> =>
    api.patch(API_ROUTES.CONVERSATIONS.BY_ID(id), { status: 'archived' }),

  // Move to trash (soft delete)
  trashConversation: async (id: string): Promise<Conversation> =>
    api.patch(API_ROUTES.CONVERSATIONS.BY_ID(id), { status: 'deleted' }),

  // Summarize conversation
  summarizeConversation: async (
    id: string,
    tone?: string,
    options?: {
      draftIntent?: 'follow_up' | 'booking_approved' | 'booking_declined';
      additionalContext?: string;
    }
  ): Promise<{ summary: string | { subject: string; body: string } }> =>
    api.post(`${API_ROUTES.CONVERSATIONS.BY_ID(id)}/summarize`, {
      tone,
      draftIntent: options?.draftIntent,
      additionalContext: options?.additionalContext,
    }),
};
